import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import { Order, OrderCartItem, OrderShort } from '~/api/Order';
import { company } from '~/company/Company';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { PartiallyPicked } from '~/components/PartiallyPicked/PartiallyPicked';
import { useDateLang } from '~/hooks/useDateLang';
import { useGlobal } from '~/hooks/useGlobal';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { mainStore } from '~/stores';
import { OrderStatusStore } from '~/stores/OrderStore';
import { formatPriceWithCurrency } from '~/utils/formaters';
import { normalizeOrderId } from '~/utils/normalizeOrderId';

import { OrdersTab } from './interfaces';
import styles from './Orders.module.scss';

type OrderItemProps = {
  order: Order | OrderShort;
  isActive?: boolean;
};

export const OrderItem = ({ order, isActive }: OrderItemProps) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();
  const [publicStatus, setPublicStatus] =
    useState<OrderStatusStore>('accepted');
  const isDelivering = order.public_status === 'in_delivery';
  const { isMobile } = useGlobal();
  const { timeFormat } = useTimeFormat();
  const isPickUpOrder =
    order.delivery_method === ETADeliveryMethodType.ClickAndCollect;

  useEffect(() => {
    let status: OrderStatusStore;
    switch (order.public_status) {
      case 'in_delivery':
        status = 'inDelivery';
        break;
      case 'ready_to_ship':
        status = 'readyToShip';
        break;
      case 'ready_for_pickup':
        status = 'readyForPickup';
        break;
      default:
        status = order.public_status;
        break;
    }
    setPublicStatus(status);
  }, []);

  const address = [
    order.address?.zip,
    order.address?.address_1,
    order.address?.address_2,
    order.address?.city,
    order.address?.country_id,
  ]
    .filter((part) => part)
    .join(', ');
  const pickUpAddress =
    'order_delivery' in order ? order.order_delivery?.warehouse_address : '';
  const displayAddress = isPickUpOrder ? pickUpAddress || address : address;

  const isPartiallyPicked = useMemo(() => {
    return order.items.some(
      (i: OrderCartItem) => i.requested_quantity - i.actual_quantity > 0,
    );
  }, [order]);

  const partiallyPickedMessage = isPartiallyPicked &&
    company.showPartiallyPickedInfo && (
      <PartiallyPicked classNames={styles.partiallyPicked} />
    );

  return (
    <Link
      to={`/order/${order.id}`}
      state={isActive ? undefined : { tab: OrdersTab.HISTORY }}
      replace={true}
      className={styles.orderItem}
    >
      <div
        className={classes([
          'order-wrapper',
          isActive && '_active',
          order.public_status === 'cancelled' && '_cancelled',
          isDelivering && '_delivering',
        ])}
      >
        <div className={classes(['header', isActive && 'activeOrder'])}>
          <div className="title-wrapper">
            <div className="title">
              {t('dayAtTime', {
                day: format(new Date(order.created_at), 'MMMM d', {
                  timeZone: company.config.timeZone,
                  locale,
                }),
                time: format(new Date(order.created_at), timeFormat, {
                  timeZone: company.config.timeZone,
                  locale,
                }),
              })}
            </div>
            <div className="subtitle">#{normalizeOrderId(order.order_id)}</div>
          </div>
          <div className="right-block">
            <div className={classes(['badge-closed', order.public_status])}>
              {t(publicStatus)}
            </div>
          </div>
        </div>
        <div className="body">
          {order.public_status !== 'cancelled' && (
            <div className="left-block">
              <>
                <div className="paid-wrapper">
                  <div className="title">{t('paid')}</div>
                  <div className="value">
                    {formatPriceWithCurrency(
                      mainStore.convertPenceToPounds(order.paid_total || 0),
                    )}
                  </div>
                </div>
                <div className="paid-wrapper">
                  <div className="title">
                    {isPickUpOrder
                      ? `${t('deliveryMethods:pickup')} ${t('address')}`
                      : t('deliveryAddress')}
                  </div>
                  <div className="address">{displayAddress}</div>
                </div>
                {!isActive && (
                  <div className="paid-wrapper">
                    <div className="title">{t('dateOfDelivery')}</div>
                    <div className="address">
                      {t('dayAtTime', {
                        day: format(new Date(order.sla.ship_by), 'MMMM d', {
                          timeZone: company.config.timeZone,
                          locale,
                        }),
                        time: format(new Date(order.sla.ship_by), timeFormat, {
                          timeZone: company.config.timeZone,
                          locale,
                        }),
                      })}
                    </div>
                  </div>
                )}
              </>
              {!isMobile && partiallyPickedMessage}
            </div>
          )}
          <div className="right-block">
            <div className="buttle-wrapper">
              {order.items
                .filter((i) => !i.bundle_id)
                .map((item) => (
                  <div key={item.image} className="buttle-wrapper-image">
                    <CloudflareResponseImage
                      src={item.image ?? ''}
                      srcParams={{ width: 54, height: 62 }}
                      srcSetParams={defaultSrcSetParams}
                      alt=""
                      loading="lazy"
                    />
                  </div>
                ))}
              {order.bundles?.map((item) => (
                <div key={item.image} className="buttle-wrapper-image">
                  <CloudflareResponseImage
                    src={item.image ?? ''}
                    srcParams={{ width: 54, height: 62 }}
                    srcSetParams={defaultSrcSetParams}
                    alt=""
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
          {isMobile && partiallyPickedMessage}
        </div>
      </div>
    </Link>
  );
};

export default observer(OrderItem);
